/**
 * Indicador de completude por etapa do wizard de admissão.
 *
 * Monitora os campos `required` de cada etapa e colore os botões
 * da barra de progresso conforme o estado da etapa:
 *
 *   Cinza  — etapa ainda não visitada
 *   Amarelo — etapa visitada com pelo menos um campo obrigatório em branco
 *   Verde  — todos os campos obrigatórios da etapa estão preenchidos
 *   Azul   — etapa atualmente ativa (gerenciado pelo wizard.js, não alterado aqui)
 *
 * @param {HTMLElement} rootEl  Elemento raiz do controller admissoes--form
 * @returns {{ destroy: Function } | null}
 */
export function initSectionProgress(rootEl) {
  const allSections = Array.from(rootEl.querySelectorAll('[data-wizard-step]'))
  if (!allSections.length) return null

  const stepBtns = Array.from(rootEl.querySelectorAll('[data-wizard-nav="step"]'))
  if (!stepBtns.length) return null

  const stepNums = [...new Set(allSections.map(s => +s.dataset.wizardStep))].sort((a, b) => a - b)

  // Em edição (form com _method=PATCH/PUT), todas as etapas são tratadas como visitadas
  // para que os indicadores de completude apareçam imediatamente.
  const isEditMode = !!rootEl.querySelector('form input[name="_method"]')
  const allStepsVisited = isEditMode || rootEl.dataset.allStepsVisited === 'true'
  const visited = new Set(allStepsVisited ? stepNums.map((_, i) => i) : [0])

  // ── helpers ──────────────────────────────────────────────────────

  function sectionsAt(n) {
    return allSections.filter(s => +s.dataset.wizardStep === stepNums[n])
  }

  /**
   * Verifica se um campo visível e habilitado tem valor preenchido.
   * Respeita acordeões colapsados (validados mesmo quando hidden).
   */
  function isVisible(el, container) {
    if (el.disabled) return false
    let node = el.parentElement
    while (node && node !== container) {
      if (node.dataset.crudAccordionTarget) { node = node.parentElement; continue }
      if (node.style?.display === 'none' || node.classList?.contains('hidden')) return false
      node = node.parentElement
    }
    return true
  }

  /** Retorna true se a etapa tem algum campo obrigatório vazio. */
  function hasEmptyRequired(stepIndex) {
    const seen = new Set()
    for (const sec of sectionsAt(stepIndex)) {
      const fields = sec.querySelectorAll(
        'input[required]:not([disabled]), select[required]:not([disabled]), textarea[required]:not([disabled])'
      )
      for (const el of fields) {
        if (!isVisible(el, sec)) continue
        const type = (el.type || '').toLowerCase()
        if (type === 'hidden') continue

        // File inputs: considera preenchido se já há anexo salvo ou arquivo selecionado
        if (type === 'file') {
          const widget = el.closest('[data-controller~="crud--upload-arquivo"]')
          if (widget && widget.querySelector('a[target="_blank"]')) continue
          if (el.files && el.files.length > 0) continue
          return true
        }

        if (type === 'radio') {
          const name = el.name
          if (!name || seen.has(name)) continue
          seen.add(name)
          const group = [...sec.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`)]
          if (!group.some(r => r.checked)) return true
          continue
        }

        if (!(el.value || '').trim()) return true
      }
    }
    return false
  }

  // ── renderização ──────────────────────────────────────────────────

  // Classes de estado (sem o estado "ativo" que pertence ao wizard.js)
  const STATE = {
    unvisited: ['bg-gray-100',    'text-gray-600',  'hover:bg-gray-200'],
    complete:  ['bg-green-600',   'text-white'],
    incomplete: ['bg-yellow-400', 'text-gray-900'],
  }

  function applyState(btn, state) {
    // Remove todas as classes de estado possíveis antes de aplicar
    btn.classList.remove(
      'bg-gray-100', 'text-gray-600', 'hover:bg-gray-200',
      'bg-green-600', 'text-white',
      'bg-yellow-400', 'text-gray-900',
      'bg-blue-600'
    )
    const circle = btn.querySelector('[data-wizard-circle]')
    if (circle) {
      // Remove todas as classes possíveis do círculo (sem background — só borda e texto)
      circle.classList.remove(
        'border-white', 'text-white',
        'border-gray-400', 'text-gray-500',
        'border-green-200', 'border-yellow-200',
        'bg-white', 'bg-green-700', 'bg-yellow-500',
        'text-blue-600', 'text-gray-900'
      )
    }

    btn.classList.add(...STATE[state])

    if (circle) {
      if (state === 'complete') {
        circle.classList.add('border-green-200', 'text-white')
      } else if (state === 'incomplete') {
        circle.classList.add('border-yellow-200', 'text-gray-900')
      } else {
        circle.classList.add('border-gray-400', 'text-gray-500')
      }
    }
  }

  /**
   * Atualiza a cor de um botão de etapa.
   * Se a etapa estiver ativa (wizard.js coloca bg-blue-600), não altera.
   */
  function refreshBtn(i) {
    const btn = stepBtns[i]
    if (!btn) return

    // Etapa ativa: wizard.js é responsável — apenas garantimos que visited está atualizado
    if (btn.classList.contains('bg-blue-600')) return

    if (!visited.has(i)) {
      applyState(btn, 'unvisited')
      return
    }

    applyState(btn, hasEmptyRequired(i) ? 'incomplete' : 'complete')
  }

  // ── observar mudanças de campo ────────────────────────────────────

  // MutationObserver detecta quando o wizard.js troca o step ativo (bg-blue-600).
  // Regra de visited: um step é marcado como visitado quando o usuário SAI dele
  // (bg-blue-600 é REMOVIDO), não quando chega (bg-blue-600 é adicionado).
  // Isso evita que clicar num step o torne verde/amarelo imediatamente.
  // Usa requestAnimationFrame para garantir que wizard.js termine de aplicar TODAS as
  // classes de updateUI() antes de refreshAll rodar.
  let _prevActive = new Set(
    stepBtns.map((btn, i) => btn.classList.contains('bg-blue-600') ? i : -1).filter(i => i >= 0)
  )
  let _rafPending = false
  const mo = new MutationObserver(() => {
    if (_rafPending) return
    _rafPending = true
    requestAnimationFrame(() => {
      _rafPending = false
      // Marca como visitado quem ERA ativo e agora DEIXOU de ser (usuário saiu)
      _prevActive.forEach(i => {
        if (!stepBtns[i]?.classList.contains('bg-blue-600')) visited.add(i)
      })
      // Atualiza conjunto de steps ativos para a próxima comparação
      _prevActive = new Set(
        stepBtns.map((btn, i) => btn.classList.contains('bg-blue-600') ? i : -1).filter(i => i >= 0)
      )
      refreshAll()
    })
  })

  function connectObserver() {
    stepBtns.forEach(btn => {
      mo.observe(btn, { attributes: true, attributeFilter: ['class'] })
    })
  }

  function refreshAll() {
    mo.disconnect()
    stepBtns.forEach((_, i) => refreshBtn(i))
    connectObserver()
  }

  connectObserver()

  // Atualiza ao editar qualquer campo do formulário
  const formEl = rootEl.querySelector('form')
  const onInput = () => refreshAll()
  formEl?.addEventListener('input', onInput)
  formEl?.addEventListener('change', onInput)

  refreshAll()

  return {
    refresh() {
      // Em edição, garante que todos os steps estão em visited antes de colorir
      // (necessário caso a API de defaults mude required e afterApply chame refresh)
      if (allStepsVisited) stepNums.forEach((_, i) => visited.add(i))
      refreshAll()
    },
    destroy() {
      mo.disconnect()
      formEl?.removeEventListener('input', onInput)
      formEl?.removeEventListener('change', onInput)
    }
  }
}
